import type { components } from './schema';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import { widgetsKey } from './widgets';
import type {
  AddAiSkillInput,
  AiAgent,
  CreateAiAgentInput,
  DataEnvelope,
  McpTestResult,
  TestMcpInput,
  UpdateAiAgentInput,
  UpdateAiSkillInput,
} from './types';

export const aiAgentsKey = ['ai-agents'] as const;

/** `ws`: a client workspace id (platform console); omitted = the workspace currently open. */
export function useAiAgents(ws?: string) {
  return useQuery({
    queryKey: [...aiAgentsKey, ws ?? 'current'],
    queryFn: ({ signal }) =>
      api<DataEnvelope<AiAgent[]>>('/ai/agents', { signal, workspaceId: ws }).then((r) => r.data),
  });
}

export function useSaveAiAgent(ws?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id?: string; input: CreateAiAgentInput | UpdateAiAgentInput }) =>
      (id
        ? api<DataEnvelope<AiAgent>>(`/ai/agents/${id}`, { method: 'PATCH', body: input, workspaceId: ws })
        : api<DataEnvelope<AiAgent>>('/ai/agents', { method: 'POST', body: input, workspaceId: ws })
      ).then((r) => r.data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: aiAgentsKey });
      void qc.invalidateQueries({ queryKey: ['platform'] });
    },
  });
}

export function useDeleteAiAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<DataEnvelope<{ unlinkedWidgets: number }>>(`/ai/agents/${id}`, { method: 'DELETE' }).then(
        (r) => r.data,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: aiAgentsKey });
      void qc.invalidateQueries({ queryKey: widgetsKey });
    },
  });
}

export function useAddAiSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ agentId, input }: { agentId: string; input: AddAiSkillInput }) =>
      api<DataEnvelope<AiAgent>>(`/ai/agents/${agentId}/skills`, { method: 'POST', body: input }).then(
        (r) => r.data,
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: aiAgentsKey }),
  });
}

export function useUpdateAiSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      agentId,
      skillId,
      input,
    }: {
      agentId: string;
      skillId: string;
      input: UpdateAiSkillInput;
    }) =>
      api<DataEnvelope<AiAgent>>(`/ai/agents/${agentId}/skills/${skillId}`, {
        method: 'PATCH',
        body: input,
      }).then((r) => r.data),
    onSuccess: () => void qc.invalidateQueries({ queryKey: aiAgentsKey }),
  });
}

export function useRemoveAiSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ agentId, skillId }: { agentId: string; skillId: string }) =>
      api<DataEnvelope<AiAgent>>(`/ai/agents/${agentId}/skills/${skillId}`, { method: 'DELETE' }).then(
        (r) => r.data,
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: aiAgentsKey }),
  });
}

export function useTestMcp() {
  return useMutation({
    mutationFn: (input: TestMcpInput) =>
      api<DataEnvelope<McpTestResult>>('/ai/mcp/test', { method: 'POST', body: input }).then((r) => r.data),
  });
}

export type TryToolInput = components['schemas']['TryToolInput'];
export type TryToolResult = components['schemas']['TryToolResult'];

/** Runs one tool of a saved skill (optionally as a signed-in user) to verify what the AI would get. */
export function useTryTool() {
  return useMutation({
    mutationFn: ({ agentId, skillId, input }: { agentId: string; skillId: string; input: TryToolInput }) =>
      api<DataEnvelope<TryToolResult>>(`/ai/agents/${agentId}/skills/${skillId}/try`, {
        method: 'POST',
        body: input,
      }).then((r) => r.data),
  });
}
